import React, { useEffect, useState } from "react";
import SideBar from "../components/SideBar";
import DetailsHeaderBar from "../components/DetailsHeaderBar";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import axios from "axios";
import { API_URL } from "../constant";

function CreateProduct() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [thumbnail, setThumbnail] = useState(null);
  const [images, setImages] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const imagesInputRef = React.useRef(null);

  const [productName, setProductName] = useState("");
  const [slug, setSlug] = useState("");
  const [sku, setSku] = useState("");
  const [category, setCategory] = useState("");
  const [subCategory, setSubCategory] = useState("");
  const [supplier, setSupplier] = useState("");
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [sellerOptions, setSellerOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);

  const [seller, setSeller] = useState("");
  const [featured, setFeatured] = useState({ value: 0, label: "No" });
  const [status, setStatus] = useState({ value: "draft", label: "Draft" });
  const [visibility, setVisibility] = useState({
    value: "Public",
    label: "Public",
  });
  const [price, setPrice] = useState("");
  const [comparePrice, setComparePrice] = useState("");
  const [cost, setCost] = useState("");
  const [discout, setDiscout] = useState("");
  const [tax, setTax] = useState("");
  const [commission, setCommission] = useState("");

  const [trackQuantity, setTrackQuantity] = useState({
    value: 1,
    label: "Yes",
  });
  const [stockQuantity, setStockQuantity] = useState("");
  const [stock, setStock] = useState("");
  const [weight, setWeight] = useState("");
  const [fullDescription, setFullDescription] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [attributes, setAttributes] = useState([{ name: "", value: "" }]);
  const [tags, setTags] = useState("");
  const [productTags, setProductTags] = useState("");
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [metaKeywords, setMetaKeywords] = useState("");

  // Remove hardcoded token and use from localStorage
  const getToken = () => localStorage.getItem("auth_token");

  const apiClient = React.useMemo(
    () =>
      axios.create({
        baseURL: API_URL || "https://api.topcoinuk.com/api",
        headers: {
          Accept: "application/json",
        },
      }),
    [],
  );

  // Add token interceptor
  apiClient.interceptors.request.use((config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Handle 401 responses
  apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        localStorage.removeItem("auth_token");
        window.location.href = "/login";
      }
      return Promise.reject(error);
    },
  );

  useEffect(() => {
    if (trackQuantity?.value === 0) {
      setStockQuantity("-");
    } else {
      if (stockQuantity === "-") setStockQuantity("");
    }
  }, [trackQuantity]);

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
    { value: "draft", label: "Draft" },
    { value: "published", label: "Published" },
    { value: "scheduled", label: "Scheduled" },
  ];

  const visibilityOptions = [
    { value: "Hidden", label: "Hidden" },
    { value: "Public", label: "Public" },
    { value: "Block", label: "Block" },
    { value: "Banned", label: "Banned" },
  ];

  const featuredOptions = [
    { value: 1, label: "Yes" },
    { value: 0, label: "No" },
  ];

  const trackQuantityOptions = [
    { value: 0, label: "No" },
    { value: 1, label: "Yes" },
  ];

  const validateBasicDetails = () => {
    let valid = true;
    return valid;
  };

  const validateStep = () => {
    if (step === 1) {
      // Add image validation if needed
    }
    return true;
  };

  const nextStep = () => {
    if (step === 1) {
      const isValid = validateBasicDetails();
      if (!isValid) return;
    }
    if (validateStep()) setStep((prev) => Math.min(prev + 1, 5));
    else alert("Please fill all required fields before proceeding.");
  };

  const prevStep = () => setStep((prev) => Math.max(prev - 1, 1));

  const steps = [
    { label: "Product image", sub: "Enter product image details" },
    { label: "Basic details", sub: "Enter product basic details" },
    {
      label: "Pricing, Tax & Commission",
      sub: "Enter product financial details",
    },
    { label: "Inventory", sub: "Enter product inventory details" },
    {
      label: "Description & Attributes",
      sub: "Enter description & attributes details",
    },
  ];

  const normalizeId = (val) => {
    if (val === undefined || val === null || val === "") return null;
    const raw = typeof val === "object" ? (val.id ?? val.value) : val;
    const num = Number(raw);
    if (Number.isFinite(num) && !Number.isNaN(num)) return num;
    return null;
  };

  const getLabel = (val) => {
    if (val === undefined || val === null || val === "") return null;
    if (typeof val === "object") return val.label ?? val.name ?? null;
    return val;
  };

  const createOptionOnServer = async (type, name, forcedCategoryId) => {
    const slugValue = name.toLowerCase().replace(/\s+/g, "_");
    const endpointMap = {
      category: "/superadmin/categories",
      subcategory: "/superadmin/subcategories",
      supplier: "/superadmin/suppliers",
      seller: "/superadmin/sellers",
    };
    const endpoint = endpointMap[type];
    if (!endpoint) return null;

    try {
      const payload = { name };
      if (type === "supplier" || type === "seller") {
        payload.retail_name = name;
      }
      if (type === "subcategory" && (forcedCategoryId || category?.value)) {
        payload.category_id = forcedCategoryId || category.value;
      }

      const res = await apiClient.post(endpoint, payload);
      const data = res.data?.[type] || res.data?.data || res.data || {};

      const resolvedId = Number(data.id);
      return {
        value: Number.isFinite(resolvedId)
          ? resolvedId
          : (data.id ?? slugValue),
        label: data.name ?? name,
        ...(type === "subcategory" && (data.category_id || payload.category_id)
          ? { categoryId: data.category_id || payload.category_id }
          : {}),
      };
    } catch (err) {
      console.error(
        `Failed to create ${type}`,
        err?.response?.data || err.message,
      );
      return null;
    }
  };

  const ensureCategoryWithId = async () => {
    const catLabel = getLabel(category);
    const catId = normalizeId(category);
    if (catId) return { id: catId, option: category };
    if (!catLabel) return { id: null, option: null };

    try {
      const created = await createOptionOnServer("category", catLabel);
      if (created?.value) {
        setCategoryOptions((prev) => [...prev, created]);
        setCategory(created);
        return { id: normalizeId(created), option: created };
      }
    } catch (err) {
      console.error(
        "Failed to auto-create category",
        err?.response?.data || err.message,
      );
    }
    return { id: null, option: category };
  };

  const ensureSubCategoryWithId = async (resolvedCategoryId) => {
    const subLabel = getLabel(subCategory);
    const subId = normalizeId(subCategory);
    if (subId) return { id: subId, option: subCategory };
    if (!subLabel) return { id: null, option: null };

    try {
      const payloadCategory = resolvedCategoryId || normalizeId(category);
      const created = await createOptionOnServer(
        "subcategory",
        subLabel,
        payloadCategory,
      );
      if (created?.value) {
        setSubCategoryOptions((prev) => [...prev, created]);
        setSubCategory(created);
        return { id: normalizeId(created), option: created };
      }
    } catch (err) {
      console.error(
        "Failed to auto-create subcategory",
        err?.response?.data || err.message,
      );
    }
    return { id: null, option: subCategory };
  };

  const ensureSupplierWithId = async () => {
    const supLabel = getLabel(supplier);
    const supId = normalizeId(supplier);
    if (supId) return { id: supId, option: supplier };
    if (!supLabel) return { id: null, option: null };

    try {
      const created = await createOptionOnServer("supplier", supLabel);
      if (created?.value) {
        setSupplierOptions((prev) => [...prev, created]);
        setSupplier(created);
        return { id: normalizeId(created), option: created };
      }
    } catch (err) {
      console.error(
        "Failed to auto-create supplier",
        err?.response?.data || err.message,
      );
    }
    return { id: null, option: supplier };
  };

  const handleCreateProduct = async () => {
    if (!validateStep()) {
      alert("Please fill all required fields before creating the product.");
      return;
    }

    // Ensure category/subcategory/supplier exist and have IDs
    const ensuredCategory = await ensureCategoryWithId();
    const ensuredSubCategory = await ensureSubCategoryWithId(
      ensuredCategory.id,
    );
    const ensuredSupplier = await ensureSupplierWithId();

    const productData = new FormData();
    const appendIfValue = (key, value) => {
      if (value !== undefined && value !== null && value !== "") {
        productData.append(key, value);
      }
    };

    appendIfValue("name", productName);
    appendIfValue("slug", slug);
    appendIfValue("sku", sku);
    appendIfValue("price", price.toString());
    appendIfValue("status", status?.value);
    appendIfValue("featured", featured?.value);
    appendIfValue("visibility", visibility?.value);

    const normalizedCategory = ensuredCategory.id ?? normalizeId(category);
    const normalizedSubCategory =
      ensuredSubCategory.id ?? normalizeId(subCategory);
    const normalizedSupplier = ensuredSupplier.id ?? normalizeId(supplier);
    const normalizedSeller = normalizeId(seller);

    if (Number.isFinite(normalizedCategory))
      appendIfValue("category_id", normalizedCategory);
    if (Number.isFinite(normalizedSubCategory))
      appendIfValue("subcategory_id", normalizedSubCategory);
    appendIfValue("category_name", getLabel(category));
    appendIfValue("subcategory_name", getLabel(subCategory));
    if (Number.isFinite(normalizedSupplier))
      appendIfValue("supplier_id", normalizedSupplier);
    if (Number.isFinite(normalizedSeller))
      appendIfValue("seller_id", normalizedSeller);

    // Always send stock_quantity and stock as numbers converted to string for FormData
    const stockQtyVal =
      trackQuantity?.value === 0 ? 0 : parseInt(stockQuantity) || 0;
    const stockVal = parseInt(stock) || 0;
    productData.append("stock_quantity", stockQtyVal.toString());
    productData.append("stock", stockVal.toString());

    appendIfValue("track_quantity", trackQuantity?.value?.toString());
    appendIfValue("compare_price", comparePrice.toString());
    appendIfValue("cost_per_item", cost.toString());
    appendIfValue("product_discount", discout.toString());
    appendIfValue("tax", tax.toString());
    appendIfValue("commission", commission.toString());
    appendIfValue("weight", weight.toString());
    appendIfValue("description", fullDescription);
    appendIfValue("short_description", shortDescription);

    // Send attributes - only include if there are valid attributes with names
    const attributesMap = attributes
      .filter((a) => a?.name && a.name.trim())
      .reduce((acc, a) => {
        const key = a.name.trim();
        acc[key] = (a.value ?? "").trim();
        return acc;
      }, {});

    if (Object.keys(attributesMap).length) {
      productData.append("attributes", JSON.stringify(attributesMap));
    }

    // Convert tags to array
    if (tags) {
      const tagsArray = tags
        .split(",")
        .map((t) => t.trim())
        .filter((t) => t);
      tagsArray.forEach((tag) => productData.append("tags[]", tag));
    }

    // Convert product_tags to array
    if (productTags) {
      const productTagsArray = productTags
        .split(",")
        .map((t) => t.trim())
        .filter((t) => t);
      productTagsArray.forEach((tag) =>
        productData.append("product_tags[]", tag),
      );
    }

    appendIfValue("meta_title", metaTitle);
    appendIfValue("meta_description", metaDescription);

    // Convert meta_keywords to array
    if (metaKeywords) {
      const keywordsArray = metaKeywords
        .split(",")
        .map((k) => k.trim())
        .filter((k) => k);
      keywordsArray.forEach((keyword) =>
        productData.append("meta_keywords[]", keyword),
      );
    }

    if (thumbnail?.file) productData.append("thumbnail", thumbnail.file);
    images.forEach((img) => {
      if (img?.file) productData.append("images[]", img.file);
    });

    try {
      const response = await apiClient.post(
        "/superadmin/products",
        productData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      setOpenSnackbar(true);

      // Clean up object URLs
      if (thumbnail?.preview) {
        URL.revokeObjectURL(thumbnail.preview);
      }
      images.forEach((img) => {
        if (img?.preview) {
          URL.revokeObjectURL(img.preview);
        }
      });

      setThumbnail(null);
      setImages([]);

      // Redirect after 3 seconds
      setTimeout(() => {
        navigate("/manage-product");
      }, 3000);
    } catch (error) {
      console.error(
        "Failed to create product:",
        error.response?.data || error.message,
      );

      const validation = error.response?.data?.errors;
      if (validation && typeof validation === "object") {
        const messages = Object.entries(validation)
          .map(
            ([field, msgs]) =>
              `${field}: ${Array.isArray(msgs) ? msgs.join("; ") : msgs}`,
          )
          .join("\n");
        alert(`Failed to create product:\n${messages}`);
      } else {
        alert("Failed to create product. Please check console for details.");
      }
    }
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setThumbnail({
      file,
      preview: URL.createObjectURL(file),
    });
  };

  const removeThumbnail = () => {
    if (thumbnail?.preview) {
      URL.revokeObjectURL(thumbnail.preview);
    }
    setThumbnail(null);
  };

  const handleImagesChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const newImages = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));

    setImages((prev) => [...prev, ...newImages]);

    // Reset the input so the same file can be selected again
    if (imagesInputRef.current) {
      imagesInputRef.current.value = "";
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setOpenSnackbar(false);
  };

  const addAttribute = () => {
    setAttributes([...attributes, { name: "", value: "" }]);
  };

  const removeAttribute = (index) => {
    setAttributes(attributes.filter((_, i) => i !== index));
  };

  const updateAttribute = (index, field, value) => {
    const updated = [...attributes];
    updated[index][field] = value;
    setAttributes(updated);
  };

  useEffect(() => {
  const loadOptions = async () => {
    try {
      const [catsRes, subsRes, suppliersRes] = await Promise.all([
        apiClient.get("/superadmin/categories"),
        apiClient.get("/superadmin/subcategories").catch(() => ({ data: {} })),
        apiClient.get("/superadmin/suppliers"),
      ]);

      console.log("Categories response:", catsRes.data);
      console.log("Subcategories response:", subsRes.data);
      console.log("Suppliers response:", suppliersRes.data);

      // Handle categories
      const categoriesPayload = catsRes.data?.categories || catsRes.data?.data || catsRes.data || [];
      const categories = Array.isArray(categoriesPayload)
        ? categoriesPayload
        : Array.isArray(categoriesPayload?.data)
        ? categoriesPayload.data
        : [];
      
      const categoryOpts = categories.map((c) => ({ 
        value: c.id, 
        label: c.name 
      }));
      setCategoryOptions(categoryOpts);

      // Handle subcategories - Try multiple possible response structures
      let allSubcategories = [];
      
      // 1. Check if subcategories are in the categories response
      if (categories.length > 0) {
        const subcatsFromCategories = categories.flatMap((cat) => {
          if (cat.subcategories && Array.isArray(cat.subcategories)) {
            return cat.subcategories.map(sub => ({
              ...sub,
              category_id: cat.id // Ensure category_id is set
            }));
          }
          return [];
        });
        allSubcategories = [...allSubcategories, ...subcatsFromCategories];
      }

      // 2. Check standalone subcategories API response
      const subsPayload = subsRes.data?.subcategories || subsRes.data?.data || subsRes.data || [];
      const subsStandalone = Array.isArray(subsPayload)
        ? subsPayload
        : Array.isArray(subsPayload?.data)
        ? subsPayload.data
        : [];
      
      // Merge both sources, removing duplicates by id
      const mergedSubs = [...allSubcategories, ...subsStandalone];
      const uniqueSubs = mergedSubs.filter(
        (sub, index, self) => 
          index === self.findIndex((s) => s.id === sub.id)
      );

      console.log("Found subcategories:", uniqueSubs);

      const subcatOpts = uniqueSubs.map((s) => ({ 
        value: s.id, 
        label: s.name, 
        categoryId: s.category_id 
      }));
      setSubCategoryOptions(subcatOpts);

      // Handle suppliers
      const suppliers = suppliersRes.data?.suppliers || suppliersRes.data?.data || suppliersRes.data || [];
      const supplierOpts = Array.isArray(suppliers)
        ? suppliers.map((s) => {
          const rawId = s.id ?? s.supplier_id;
          const numericId = Number.isFinite(Number(rawId)) ? Number(rawId) : rawId;
          return {
            value: numericId,
            label: s.retail_name || s.name || `Supplier ${rawId ?? ""}`,
          };
        })
        : [];
      setSupplierOptions(supplierOpts);

      // Mirror supplier list into seller options
      setSellerOptions(supplierOpts);
    } catch (err) {
      console.error("Failed to load dropdown options", err?.response?.data || err.message);
    }
  };

  loadOptions();
}, [apiClient]);

  // Keep seller options in sync with suppliers after fetch
  useEffect(() => {
    setSellerOptions(supplierOptions);
  }, [supplierOptions]);

  return (
    <div className="manage-member-layout">
      <SideBar />
      <div className="product-details-main">
        <Snackbar
          open={openSnackbar}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MuiAlert
            severity="success"
            icon={
              <span
                style={{
                  fontWeight: "bold",
                  fontSize: "18px",
                  color: "rgba(51, 178, 51, 1",
                }}
              >
                ✔
              </span>
            }
            sx={{
              backgroundColor: "transparent",
              backdropFilter: "blur(8px)",
              color: "#000",
              border: "2px solid rgba(51, 178, 51, 1)",
              borderRadius: "10px",
              boxShadow: "none",
              fontSize: "15px",
              padding: "8px 12px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              height: "25px",
              width: "100%",
              textAlign: "center",
              justifyContent: "center",
            }}
          >
            Product created successfully!
          </MuiAlert>
        </Snackbar>

        <DetailsHeaderBar
          title="Create New Product"
          subtitle="After creating a new product, you can manage it."
          breadcrumbs={[
            { label: "Home", path: "/manage-member" },
            { label: "Manage Product", path: "/manage-product" },
            { label: "Create New Product" },
          ]}
        />

        <div className="multi-form-container">
          <div className="progress-bar">
            {steps.map((item, i) => {
              const stepNum = i + 1;
              const isActive = step === stepNum;
              const isCompleted = step > stepNum;
              return (
                <div key={i} className="progress-step">
                  <div
                    className={`circle ${
                      isCompleted ? "completed" : isActive ? "active" : ""
                    }`}
                  >
                    {isCompleted ? "✓" : stepNum}
                  </div>
                  <div className="step-text">
                    <p
                      className={`step-label ${isActive ? "active-label" : ""}`}
                    >
                      {item.label}
                    </p>
                    <p
                      className={`step-subtext ${isActive ? "active-sub" : ""}`}
                    >
                      {item.sub}
                    </p>
                  </div>
                  {i < steps.length - 1 && (
                    <div
                      className={`line ${
                        isCompleted ? "line-filled" : "line-empty"
                      }`}
                    ></div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Step content */}
          <div className="step-content">
            {step === 1 && (
              <div>
                <h3>Product Image Details</h3>
                <hr />
                <div className="form-grid">
                  <div className="form-group">
                    <label>
                      Thumbnail<span className="required"></span>
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleThumbnailChange}
                    />

                    {thumbnail && (
                      <div
                        style={{
                          marginTop: 10,
                          position: "relative",
                          display: "inline-block",
                        }}
                      >
                        <img
                          src={thumbnail.preview}
                          alt="Thumbnail Preview"
                          onClick={() => setPreviewImage(thumbnail.preview)}
                          style={{
                            width: 150,
                            height: 150,
                            objectFit: "cover",
                            borderRadius: 6,
                            cursor: "pointer",
                          }}
                        />

                        <button
                          type="button"
                          onClick={removeThumbnail}
                          style={{
                            position: "absolute",
                            top: -10,
                            right: -10,
                            background: "#e53935",
                            color: "#fff",
                            border: "none",
                            borderRadius: "50%",
                            width: 24,
                            height: 24,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "bold",
                          }}
                        >
                          x
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-grid">
                  <div className="form-group">
                    <label>
                      Product Image <span className="required"></span>
                    </label>
                    <input
                      ref={imagesInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImagesChange}
                      style={{ maxWidth: "100%" }}
                    />

                    <div
                      style={{
                        display: "flex",
                        gap: 10,
                        marginTop: 10,
                        flexWrap: "wrap",
                      }}
                    >
                      {images.map((img, index) => (
                        <div
                          key={index}
                          style={{
                            position: "relative",
                            width: 120,
                            height: 120,
                          }}
                        >
                          <img
                            src={img.preview}
                            alt="product"
                            onClick={() => setPreviewImage(img.preview)}
                            style={{
                              width: 120,
                              height: 120,
                              objectFit: "cover",
                              borderRadius: 6,
                              cursor: "pointer",
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              if (img.preview) {
                                URL.revokeObjectURL(img.preview);
                              }
                              setImages(images.filter((_, i) => i !== index));
                            }}
                            style={{
                              position: "absolute",
                              top: 4,
                              right: 4,
                              background: "#e53935",
                              color: "#fff",
                              border: "none",
                              borderRadius: "50%",
                              width: 24,
                              height: 24,
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: "bold",
                              fontSize: 14,
                            }}
                          >
                            x
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Basic Details */}
            {step === 2 && (
              <div>
                <h3>Basic Details</h3>
                <hr />
                <div className="form-grid">
                  <div className="form-group">
                    <label>
                      Product name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={productName}
                      onChange={(e) => setProductName(e.target.value)}
                      placeholder="Enter product name"
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Product slug <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={slug}
                      onChange={(e) => setSlug(e.target.value)}
                      placeholder="Enter slug"
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Product SKU <span className="required"></span>
                    </label>
                    <input
                      type="text"
                      value={sku}
                      onChange={(e) => setSku(e.target.value)}
                      placeholder="Enter sku"
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Product category <span className="required"></span>
                    </label>
                    <CreatableSelect
                      options={categoryOptions}
                      value={category}
                      onChange={setCategory}
                      onCreateOption={(inputValue) => {
                        const newOption = {
                          value: inputValue,
                          label: inputValue,
                        };
                        setCategoryOptions((prev) => [...prev, newOption]);
                        setCategory(newOption);
                      }}
                      placeholder="Select or type category"
                      isSearchable
                      classNamePrefix="custom-select"
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: "1.17px solid rgba(209, 209, 209, 1)",
                          borderRadius: "17.6px",
                          background: "rgba(253, 253, 253, 1)",
                          minHeight: "38px",
                          boxShadow: state.isFocused ? "none" : "none",
                          "&:hover": "none",
                          fontSize: "14px",
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: "0 8px",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: "black",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "#fff",
                          borderRadius: "6px",
                          marginTop: "2px",
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? "#e6f0ff"
                            : isFocused
                              ? "#e6f0ff"
                              : "#fff",
                          color: isSelected ? "#000" : "#000",
                          cursor: "pointer",
                        }),
                      }}
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Product subcategory <span className="required"></span>
                    </label>
                    <CreatableSelect
                      options={subCategoryOptions}
                      value={subCategory}
                      onChange={setSubCategory}
                      onCreateOption={(inputValue) => {
                        const newOption = {
                          value: inputValue,
                          label: inputValue,
                        };
                        setSubCategoryOptions((prev) => [...prev, newOption]);
                        setSubCategory(newOption);
                      }}
                      placeholder="Select or type sub category name"
                      isSearchable
                      classNamePrefix="custom-select"
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: "1.17px solid rgba(209, 209, 209, 1)",
                          borderRadius: "17.6px",
                          background: "rgba(253, 253, 253, 1)",
                          minHeight: "38px",
                          boxShadow: state.isFocused ? "none" : "none",
                          "&:hover": "none",
                          fontSize: "14px",
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: "0 8px",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: "black",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "#fff",
                          borderRadius: "6px",
                          marginTop: "2px",
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? "#e6f0ff"
                            : isFocused
                              ? "#e6f0ff"
                              : "#fff",
                          color: isSelected ? "#000" : "#000",
                          cursor: "pointer",
                        }),
                      }}
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Product supplier <span className="required"></span>
                    </label>
                    <CreatableSelect
                      options={supplierOptions}
                      value={supplier}
                      onChange={setSupplier}
                      onCreateOption={(inputValue) => {
                        const newOption = {
                          value: inputValue,
                          label: inputValue,
                        };
                        setSupplierOptions((prev) => [...prev, newOption]);
                        setSupplier(newOption);
                      }}
                      placeholder="Select or type supplier name"
                      isSearchable
                      classNamePrefix="custom-select"
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: "1.17px solid rgba(209, 209, 209, 1)",
                          borderRadius: "17.6px",
                          background: "rgba(253, 253, 253, 1)",
                          minHeight: "38px",
                          boxShadow: state.isFocused ? "none" : "none",
                          "&:hover": "none",
                          fontSize: "14px",
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: "0 8px",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: "black",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "#fff",
                          borderRadius: "6px",
                          marginTop: "2px",
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? "#e6f0ff"
                            : isFocused
                              ? "#e6f0ff"
                              : "#fff",
                          color: isSelected ? "#000" : "#000",
                          cursor: "pointer",
                        }),
                      }}
                    />
                  </div>

                  {/* <div className="form-group">
                    <label>
                      Product seller <span className="required"></span>
                    </label>
                    <CreatableSelect
                      options={sellerOptions}
                      value={seller}
                      onChange={setSeller}
                      onCreateOption={async (inputValue) => {
                        const newOption = await createOptionOnServer(
                          "seller",
                          inputValue,
                        );
                        if (!newOption?.value) {
                          alert("Failed to create seller. Please try again.");
                          return;
                        }
                        setSellerOptions((prev) => [...prev, newOption]);
                        setSeller(newOption);
                      }}
                      placeholder="Select or type seller name"
                      isSearchable
                      classNamePrefix="custom-select"
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: "1.17px solid rgba(209, 209, 209, 1)",
                          borderRadius: "17.6px",
                          background: "rgba(253, 253, 253, 1)",
                          minHeight: "38px",
                          boxShadow: state.isFocused ? "none" : "none",
                          "&:hover": "none",
                          fontSize: "14px",
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: "0 8px",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: "black",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "#fff",
                          borderRadius: "6px",
                          marginTop: "2px",
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? "#e6f0ff"
                            : isFocused
                              ? "#e6f0ff"
                              : "#fff",
                          color: isSelected ? "#000" : "#000",
                          cursor: "pointer",
                        }),
                      }}
                    />
                  </div> */}

                  <div className="form-group">
                    <label>
                      Product featured <span className="required">*</span>
                    </label>
                    <Select
                      options={featuredOptions}
                      value={featured}
                      onChange={setFeatured}
                      placeholder="No"
                      isSearchable
                      classNamePrefix="custom-select"
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: "1.17px solid rgba(209, 209, 209, 1)",
                          borderRadius: "17.6px",
                          background: "rgba(253, 253, 253, 1)",
                          minHeight: "38px",
                          boxShadow: state.isFocused ? "none" : "none",
                          "&:hover": "none",
                          fontSize: "14px",
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: "0 8px",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: "black",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "#fff",
                          borderRadius: "6px",
                          marginTop: "2px",
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? "#e6f0ff"
                            : isFocused
                              ? "#e6f0ff"
                              : "#fff",
                          color: isSelected ? "#000" : "#000",
                          cursor: "pointer",
                        }),
                      }}
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Status <span className="required">*</span>
                    </label>
                    <Select
                      options={statusOptions}
                      value={status}
                      onChange={setStatus}
                      placeholder="Draft"
                      isSearchable
                      classNamePrefix="custom-select"
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: "1.17px solid rgba(209, 209, 209, 1)",
                          borderRadius: "17.6px",
                          background: "rgba(253, 253, 253, 1)",
                          minHeight: "38px",
                          boxShadow: state.isFocused ? "none" : "none",
                          "&:hover": "none",
                          fontSize: "14px",
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: "0 8px",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: "black",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "#fff",
                          borderRadius: "6px",
                          marginTop: "2px",
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? "#e6f0ff"
                            : isFocused
                              ? "#e6f0ff"
                              : "#fff",
                          color: isSelected ? "#000" : "#000",
                          cursor: "pointer",
                        }),
                      }}
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Visibility <span className="required">*</span>
                    </label>
                    <Select
                      options={visibilityOptions}
                      value={visibility}
                      onChange={setVisibility}
                      placeholder="Public"
                      isSearchable
                      classNamePrefix="custom-select"
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: "1.17px solid rgba(209, 209, 209, 1)",
                          borderRadius: "17.6px",
                          background: "rgba(253, 253, 253, 1)",
                          minHeight: "38px",
                          boxShadow: state.isFocused ? "none" : "none",
                          "&:hover": "none",
                          fontSize: "14px",
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: "0 8px",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: "black",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "#fff",
                          borderRadius: "6px",
                          marginTop: "2px",
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? "#e6f0ff"
                            : isFocused
                              ? "#e6f0ff"
                              : "#fff",
                          color: isSelected ? "#000" : "#000",
                          cursor: "pointer",
                        }),
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Pricing, Tax & Commission */}
            {step === 3 && (
              <div>
                <h3>Pricing, Tax & Commission Details</h3>
                <hr />
                <div className="form-grid">
                  <div className="form-group">
                    <label>
                      Price <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      placeholder="Enter price"
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Compare price <span className="required"></span>
                    </label>
                    <input
                      type="text"
                      value={comparePrice}
                      onChange={(e) => setComparePrice(e.target.value)}
                      placeholder="Enter compare price"
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Cost <span className="required"></span>
                    </label>
                    <input
                      type="text"
                      value={cost}
                      onChange={(e) => setCost(e.target.value)}
                      placeholder="Enter cost"
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Discount <span className="required"></span>
                    </label>
                    <input
                      type="text"
                      value={discout}
                      onChange={(e) => setDiscout(e.target.value)}
                      placeholder="Enter discount %"
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Tax <span className="required"></span>
                    </label>
                    <input
                      type="text"
                      value={tax}
                      onChange={(e) => setTax(e.target.value)}
                      placeholder="Enter tax %"
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Commission <span className="required"></span>
                    </label>
                    <input
                      type="text"
                      value={commission}
                      onChange={(e) => setCommission(e.target.value)}
                      placeholder="Enter commission %"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Inventory */}
            {step === 4 && (
              <div>
                <h3>Inventory Details</h3>
                <hr />
                <div className="form-grid">
                  <div className="form-group">
                    <label>
                      Track quantity <span className="required">*</span>
                    </label>
                    <Select
                      options={trackQuantityOptions}
                      value={trackQuantity}
                      onChange={setTrackQuantity}
                      placeholder="Yes"
                      isSearchable
                      classNamePrefix="custom-select"
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: "1.17px solid rgba(209, 209, 209, 1)",
                          borderRadius: "17.6px",
                          background: "rgba(253, 253, 253, 1)",
                          minHeight: "38px",
                          boxShadow: state.isFocused ? "none" : "none",
                          "&:hover": "none",
                          fontSize: "14px",
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: "0 8px",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: "black",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "#fff",
                          borderRadius: "6px",
                          marginTop: "2px",
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? "#e6f0ff"
                            : isFocused
                              ? "#e6f0ff"
                              : "#fff",
                          color: isSelected ? "#000" : "#000",
                          cursor: "pointer",
                        }),
                      }}
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Stock quantity <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={trackQuantity?.value === 0 ? "-" : stockQuantity}
                      disabled={trackQuantity?.value === 0}
                      onChange={(e) => setStockQuantity(e.target.value)}
                      placeholder={
                        trackQuantity?.value === 0
                          ? "0"
                          : "Enter stock quantity"
                      }
                      style={{
                        backgroundColor:
                          trackQuantity?.value === 0 ? "#f5f5f5" : "#fff",
                        cursor:
                          trackQuantity?.value === 0 ? "not-allowed" : "text",
                      }}
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Stock <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={stock}
                      onChange={(e) => setStock(e.target.value)}
                      placeholder="Enter stock"
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Weight <span className="required"></span>
                    </label>
                    <input
                      type="text"
                      value={weight}
                      onChange={(e) => setWeight(e.target.value)}
                      placeholder="Enter weight"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 5: Description & Attributes */}
            {step === 5 && (
              <div>
                <h3>Description & Attributes Details</h3>
                <hr />
                <div className="form-grid">
                  <div className="form-group">
                    <label>
                      Full description <span className="required"></span>
                    </label>
                    <textarea
                      className="note-textarea"
                      rows="3"
                      value={fullDescription}
                      onChange={(e) => setFullDescription(e.target.value)}
                      placeholder="enter full description here"
                      style={{ padding: "10px" }}
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Short description <span className="required"></span>
                    </label>
                    <textarea
                      className="note-textarea"
                      rows="3"
                      value={shortDescription}
                      onChange={(e) => setShortDescription(e.target.value)}
                      placeholder="enter short description here"
                      style={{ padding: "10px" }}
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Attributes <span className="required"></span>
                    </label>
                    {attributes.map((attr, index) => (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          gap: 10,
                          marginBottom: 8,
                          alignItems: "center",
                        }}
                      >
                        <input
                          type="text"
                          placeholder="Attribute name (e.g. Color)"
                          value={attr.name}
                          onChange={(e) =>
                            updateAttribute(index, "name", e.target.value)
                          }
                        />
                        <input
                          type="text"
                          placeholder="Attribute value (e.g. Black, Blue)"
                          value={attr.value}
                          onChange={(e) =>
                            updateAttribute(index, "value", e.target.value)
                          }
                        />
                        {attributes.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeAttribute(index)}
                            style={{
                              background: "#e53935",
                              color: "#fff",
                              border: "none",
                              borderRadius: 6,
                              padding: "6px 10px",
                              cursor: "pointer",
                            }}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addAttribute}
                      style={{
                        marginTop: 6,
                        background: "#1976d2",
                        color: "#fff",
                        border: "none",
                        borderRadius: 6,
                        padding: "6px 12px",
                        cursor: "pointer",
                      }}
                    >
                      + Add Attribute
                    </button>
                  </div>

                  <div className="form-group">
                    <label>
                      Tags <span className="required"></span>
                    </label>
                    <textarea
                      className="note-textarea"
                      rows="3"
                      value={tags}
                      onChange={(e) => setTags(e.target.value)}
                      placeholder="enter tags here"
                      style={{ padding: "10px" }}
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Product tags <span className="required"></span>
                    </label>
                    <textarea
                      className="note-textarea"
                      rows="3"
                      value={productTags}
                      onChange={(e) => setProductTags(e.target.value)}
                      placeholder="enter product tags here"
                      style={{ padding: "10px" }}
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Meta title <span className="required"></span>
                    </label>
                    <textarea
                      className="note-textarea"
                      rows="3"
                      value={metaTitle}
                      onChange={(e) => setMetaTitle(e.target.value)}
                      placeholder="enter meta title here"
                      style={{ padding: "10px" }}
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Meta description <span className="required"></span>
                    </label>
                    <textarea
                      className="note-textarea"
                      rows="3"
                      value={metaDescription}
                      onChange={(e) => setMetaDescription(e.target.value)}
                      placeholder="enter meta description here"
                      style={{ padding: "10px" }}
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Meta keywords <span className="required"></span>
                    </label>
                    <textarea
                      className="note-textarea"
                      rows="3"
                      value={metaKeywords}
                      onChange={(e) => setMetaKeywords(e.target.value)}
                      placeholder="enter meta keywords here"
                      style={{ padding: "10px" }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="btn-row">
            {step > 1 && (
              <button className="btn prev" onClick={prevStep}>
                Previous
              </button>
            )}
            {step < 5 && (
              <button
                className="btn next"
                onClick={nextStep}
                disabled={!validateStep()}
                style={{
                  opacity: validateStep() ? 1 : 0.6,
                  cursor: validateStep() ? "pointer" : "not-allowed",
                }}
              >
                Next
              </button>
            )}
            {step === 5 && (
              <button
                className="btn next"
                onClick={handleCreateProduct}
                disabled={!validateStep()}
                style={{
                  opacity: validateStep() ? 1 : 0.6,
                  cursor: validateStep() ? "pointer" : "not-allowed",
                }}
              >
                Create Product
              </button>
            )}
          </div>

          {previewImage && (
            <div
              onClick={() => setPreviewImage(null)}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                background: "rgba(0,0,0,0.8)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 9999,
              }}
            >
              <div style={{ position: "relative" }}>
                <img
                  src={previewImage}
                  alt="Preview"
                  style={{
                    maxWidth: "90vw",
                    maxHeight: "90vh",
                    borderRadius: 10,
                  }}
                />
                <button
                  onClick={() => setPreviewImage(null)}
                  style={{
                    position: "absolute",
                    top: -10,
                    right: -10,
                    background: "#e53935",
                    color: "#fff",
                    border: "none",
                    borderRadius: "50%",
                    width: 30,
                    height: 30,
                    cursor: "pointer",
                    fontSize: 16,
                    fontWeight: "bold",
                  }}
                >
                  ✕
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CreateProduct;
